import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { _post } from '../../../utils/network';
import { commafy } from '../../../utils/methods';

import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb.component';
import FormField from "../../../components/Form/FormField/FormField.component";
import LoadingButton from '../../../components/LoadingButton/LoadingButton.component';
import ListTable from '../../../components/List/ListTable/ListTable.component';
import Pagination from '../../../components/List/Pagination/Pagination.component';

const ProductsList = () => {
	const { t } = useTranslation();
	const breadcrumbItems = [
		{ title: t('dashboard'), link: '/dashboard' },
		{ title: t('products'), link: '/products-list' },
		{ title: t('list') }
	];

	const [products, setProducts] = useState([]);
	let productsListData = {
		head: [t('title'), t('category'), t('user_category'), t('price'), t('price_unit'), t('special'), t('status'), t('operation')],
		body: products.map(product => [
			product.product_name,
			<Link className='' to={`/product-categories-edit/${product.productcategory_id_information.productcategory_id}`} target="_blank">
				{product.productcategory_id_information.productcategory_name}
			</Link>,
			<Link className='' to={`/user-categories-edit/${product.usercategory_id_information.usercategory_id}`} target="_blank">
				{product.usercategory_id_information.usercategory_name}
			</Link>,
			commafy(product.product_price),
			product.price_unit === 'zar' ? t('score2_name') : `${t('score1_name')} ${t('and')} ${t('score2_name')}`,
			<span className={"badge rounded-pill p-2 badge-subtle-" + (product.is_special ? 'warning' : 'secondary')}>
				{product.is_special ? t('yes') : t('no')}
			</span>,
			<span className={"badge rounded-pill p-2 badge-subtle-" + (product.is_active ? 'success' : 'secondary')}>
				{product.is_active ? t('active') : t('deactive')}
			</span>,
			<Link className='btn btn-secondary btn-sm p-1' to={`/products-edit/${product.product_id}`}>
				<i className="fa fa-edit"></i> {t('edit')}
			</Link>
		]),
		directions: ['rtl', 'rtl', 'rtl', '', 'rtl', '', '', 'ltr']
	};
	
	const [pageNumber, setPageNumber] = useState(1);
	const [pageCounter] = useState(10);
	const [totalCount, setTotalCount] = useState(0);
	const [pagesCount, setPagesCount] = useState(0);
	const [searchProductTitle, setSearchProductTitle] = useState(null);
	const [searchProductCategory, setSearchProductCategory] = useState(null);
	const [searchProductUserCategory, setSearchProductUserCategory] = useState(null);
	const [searchProductStatus, setSearchProductStatus] = useState(null);
	const [searchProductIsSpecial, setSearchProductIsSpecial] = useState(null);
	const [searchButtonIsLoading, setSearchButtonIsLoading] = useState(false);
	const [searchCounter, setSearchCounter] = useState(0);
	const getProducts = async () => {
		let body = {
			product_name: searchProductTitle,
			productcategory_id: searchProductCategory,
			usercategory_id: searchProductUserCategory,
			is_active: searchProductStatus,
			is_promote: null,
			is_special: searchProductIsSpecial === null ? null : searchProductIsSpecial === 'true',
			page: pageNumber,
			count: pageCounter
		};
		setSearchButtonIsLoading(true);
		let response = await _post('/products/admin/getAll', body);
		setSearchButtonIsLoading(false);
		switch(response.code){
			case 200:
				setTotalCount(response.data.all_products);
				setProducts(response.data.products);
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};
	
	const filterProducts = event => {
		event.preventDefault();
		if(searchButtonIsLoading)
			return 0;
		let categoryID = event.target.searchProductCategory.value.trim() || null;
		let userCategoryID = event.target.searchProductUserCategory.value.trim() || null;
		let status = event.target.searchProductStatus.value.trim() || null;
		let isSpecial = event.target.searchProductIsSpecial.value.trim() || null;
		setSearchProductCategory(categoryID);
		setSearchProductUserCategory(userCategoryID);
		setSearchProductStatus(status);
		setSearchProductIsSpecial(isSpecial);
		if(pageNumber === 1)
			setSearchCounter(searchCounter + 1);
		else
			setPageNumber(1);
	};

	const [productCategories, setProductCategories] = useState([]);
	const getProductCategories = async () => {
		let body = {
			productcategory_name: null,
			page: 1,
			count: 1000000
		};
		let response = await _post('/productcategories/admin/getAll', body);
		switch(response.code){
			case 200:
				setProductCategories(response.data.productcategories.map(item => {
					return {
						key: item.productcategory_name,
						value: item.productcategory_id
					}
				}));
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};

	const [userCategories, setUserCategories] = useState([]);
	const getUserCategories = async () => {
		let body = {
			usercategory_name: null,
			page: 1,
			count: 1000000
		};
		let response = await _post('/usercategories/admin/getAll', body);
		switch(response.code){
			case 200:
				setUserCategories(response.data.usercategories.map(item => {
					return {
						key: item.usercategory_name,
						value: item.usercategory_id
					}
				}));
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};
	
	const productStatusList = [
		{ key: t('deactive'), value: 0 },
		{ key: t('active'), value: 1 }
	];

	const productIsSpecialList = [
		{ key: t('no'), value: 'false' },
		{ key: t('yes'), value: 'true' }
	];

	useEffect(() => {
		getProductCategories();
		getUserCategories();
	}, []);

	useEffect(() => {
		setPagesCount(Math.ceil(totalCount / pageCounter));
	}, [totalCount]);
	
	useEffect(() => {
		setSearchCounter(searchCounter + 1);
	}, [pageNumber]);

	useEffect(() => {
		getProducts();
	}, [searchCounter]);
	
	return (
		<>
			<Breadcrumb items={breadcrumbItems} />
			<div className="card mb-3">
				<div className="card-header">
					<h5 className="mb-0">{t('search_for')} {t('products')}</h5>
				</div>
				<div className="card-body bg-light">
					<form onSubmit={filterProducts}>
						<div className="row gx-2">
							<FormField type="select" fieldKey="searchProductCategory" title={t('category')} value={searchProductCategory} selectItems={productCategories} isSearch={true} />
							<FormField type="select" fieldKey="searchProductUserCategory" title={t('user_category')} value={searchProductUserCategory} selectItems={userCategories} isSearch={true} />
							<FormField type="select" fieldKey="searchProductStatus" title={t('status')} value={searchProductStatus} selectItems={productStatusList} isSearch={true} />
							<FormField type="select" fieldKey="searchProductIsSpecial" title={t('special')} value={searchProductIsSpecial} selectItems={productIsSpecialList} isSearch={true} />
						</div>
						<div>
							<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name="create" isLoading={searchButtonIsLoading}>
								<i className="fa fa-search"></i> {t('search')}
							</LoadingButton>
						</div>
					</form>
				</div>
			</div>
			<ListTable title={t('products')} pageNumber={pageNumber} pageCounter={pageCounter} totalCount={totalCount} data={productsListData} />
			<Pagination pagesCount={pagesCount} action={setPageNumber} />
		</>
	);
};

export default ProductsList;
